import * as React from 'react';
import { Copy, Crown } from '@phosphor-icons/react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage, AvatarBadge } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';

import { cn } from '@/lib/utils';

export type AccountDropdownActionItem = {
  type?: 'action';
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  /** Second line (e.g. "ID: xyz"); copy button shown when copyValue is set. */
  secondaryLabel?: React.ReactNode;
  copyValue?: string;
  onCopy?: (value: string) => void;
  /** Keyboard shortcut hint (e.g. "⌘K") shown on the right. */
  shortcut?: string;
};

export type AccountDropdownSubOption = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  /** Required for radio mode. */
  value?: string;
  /** Used in plain (non-radio) mode. */
  onClick?: () => void;
  /** Keyboard shortcut hint (e.g. "⌘K") shown on the right. */
  shortcut?: string;
};

/** Sub-option with a required `value`, used in radio-mode submenus. */
export type AccountDropdownRadioSubOption = Omit<AccountDropdownSubOption, 'value' | 'onClick'> & {
  value: string;
};

type AccountDropdownSubmenuBase = {
  type: 'submenu';
  id: string;
  label: string;
  icon?: React.ReactNode;
  /** Second line on the submenu trigger (e.g. "ID: xyz"); copy button when copyValue is set. */
  secondaryLabel?: React.ReactNode;
  copyValue?: string;
  onCopy?: (value: string) => void;
};

export type AccountDropdownSubmenuItem = AccountDropdownSubmenuBase &
  (
    | {
        /** The currently selected value. When provided with onValueChange, renders items as a radio group with selection indicators. */
        value: string;
        onValueChange: (value: string) => void;
        items: AccountDropdownRadioSubOption[];
      }
    | {
        value?: never;
        onValueChange?: never;
        items: AccountDropdownSubOption[];
      }
  );

export type AccountDropdownItem = AccountDropdownActionItem | AccountDropdownSubmenuItem;

export type AccountDropdownTeam = {
  id: string;
  name: string;
  isCurrent: boolean;
  logoUrl?: string;
};

export interface AccountDropdownProps extends React.ComponentPropsWithoutRef<typeof DropdownMenu> {
  accountPhoto: string;
  accountName: string;
  /** Pass a flat array for a single group, or an array of arrays for multiple groups separated by dividers. */
  accountDropdownItems: AccountDropdownItem[] | AccountDropdownItem[][];
  teams: AccountDropdownTeam[];
  /** Side the dropdown opens on relative to the trigger. Defaults to 'bottom'. */
  side?: React.ComponentPropsWithoutRef<typeof DropdownMenuContent>['side'];
  /** Alignment of the dropdown relative to the trigger. Defaults to 'end'. */
  align?: React.ComponentPropsWithoutRef<typeof DropdownMenuContent>['align'];
  isSuperAdmin?: boolean;
}

export function AccountDropdown({
  accountPhoto,
  accountName,
  accountDropdownItems,
  teams,
  side = 'bottom',
  align = 'end',
  isSuperAdmin = false,
  ...props
}: AccountDropdownProps) {
  const showTeamName = teams?.length > 1 || isSuperAdmin;
  const currentTeamName = (showTeamName && teams?.find((el) => el.isCurrent)?.name) || '';
  const initial = accountName[0];

  const groups: AccountDropdownItem[][] = Array.isArray(accountDropdownItems[0])
    ? (accountDropdownItems as AccountDropdownItem[][])
    : [accountDropdownItems as AccountDropdownItem[]];

  const handleCopy = React.useCallback((value: string, onCopy?: (value: string) => void) => {
    return (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onCopy) onCopy(value);
      else navigator.clipboard.writeText(value);
    };
  }, []);

  const renderCopyRow = (
    secondaryLabel: React.ReactNode,
    copyValue: string | undefined,
    onCopy?: (value: string) => void,
  ) =>
    secondaryLabel && (
      <div className="flex w-full items-center gap-1 text-xs text-subtlest ml-7">
        {secondaryLabel}
        {copyValue != null && (
          <Button
            variant="ghost"
            size="icon"
            className="size-4 shrink-0 p-0 [&>svg]:!size-3"
            onClick={handleCopy(copyValue, onCopy)}
            aria-label="Copy ID"
          >
            <Copy className="size-3" />
          </Button>
        )}
      </div>
    );

  return (
    <DropdownMenu {...props}>
      <DropdownMenuTrigger asChild>
        <Button
          size="default"
          variant="ghost"
          className="rounded-full p-[1px] border border-dark-300 relative"
          aria-label="Toggle account dropdown"
        >
          <Avatar>
            <AvatarImage
              src={accountPhoto}
              alt={`${accountName}'s account`}
              className={cn(
                'block rounded-full border border-background',
                isSuperAdmin && 'border-2 border-solid border-red-700 inset-1',
              )}
            />
            <AvatarFallback>{initial}</AvatarFallback>
            {isSuperAdmin && (
              <AvatarBadge className="bg-red-700 text-white">
                <Crown />
              </AvatarBadge>
            )}
          </Avatar>
          {showTeamName && <span className="text-xs text-default font-semibold pr-1">{currentTeamName}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={side}
        align={align}
        className="border-none shadow-[2px_4px_12px_0px_rgba(0,0,0,0.14)] dark:shadow-[2px_4px_12px_0px_rgba(255,255,255,0.14)]"
      >
        {groups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && <DropdownMenuSeparator />}
            <DropdownMenuGroup>
              {group.map((item) =>
                item.type === 'submenu' ? (
                  <DropdownMenuSub key={item.id}>
                    <DropdownMenuSubTrigger
                      secondaryRow={renderCopyRow(item.secondaryLabel, item.copyValue, item.onCopy)}
                    >
                      {item.icon}
                      {item.label}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent className="border-none shadow-[2px_4px_12px_0px_rgba(0,0,0,0.14)] dark:shadow-[2px_4px_12px_0px_rgba(255,255,255,0.14)]">
                        {item.value != null && item.onValueChange ? (
                          <DropdownMenuRadioGroup value={item.value} onValueChange={item.onValueChange}>
                            {item.items.map((option) => (
                              <DropdownMenuRadioItem key={option.id} value={option.value}>
                                {option.icon}
                                {option.label}
                                {option.shortcut != null && (
                                  <DropdownMenuShortcut>{option.shortcut}</DropdownMenuShortcut>
                                )}
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        ) : (
                          item.items.map((option) => (
                            <DropdownMenuItem key={option.id} onClick={option.onClick}>
                              {option.icon}
                              {option.label}
                              {option.shortcut != null && (
                                <DropdownMenuShortcut>{option.shortcut}</DropdownMenuShortcut>
                              )}
                            </DropdownMenuItem>
                          ))
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                ) : (
                  <DropdownMenuItem
                    key={item.id}
                    onClick={item.onClick}
                    secondaryRow={renderCopyRow(item.secondaryLabel, item.copyValue, item.onCopy)}
                  >
                    {item.icon}
                    {item.label}
                    {item.shortcut != null && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
                  </DropdownMenuItem>
                ),
              )}
            </DropdownMenuGroup>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
